import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContextualTooltip from '../ContextualTooltip';
import { useEscapeKey } from '../../hooks/useEscapeKey';

function ShareModal({
  isOpen,
  onClose,
  canPublicShare,
  shareToken,
  shareLoading,
  shareCopied,
  onGenerateShareLink,
  onRevokeShareLink,
  onCopyShareLink,
}) {
  const navigate = useNavigate();
  const generateBtnRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // Handle escape key
  useEscapeKey(isOpen, onClose);

  // Show tooltip when modal opens and user can generate a link
  useEffect(() => {
    if (isOpen && canPublicShare && !shareToken) {
      // Small delay to ensure button is rendered
      const timer = setTimeout(() => setShowTooltip(true), 100);
      return () => clearTimeout(timer);
    } else {
      setShowTooltip(false);
    }
  }, [isOpen, canPublicShare, shareToken]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Share List</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {!canPublicShare ? (
            <div className="share-premium-prompt">
              <div className="share-premium-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Premium Feature</h3>
              <p>Create public share links so anyone can view your list without logging in.</p>
              <button
                className="btn-primary"
                onClick={() => {
                  onClose();
                  navigate('/settings/premium');
                }}
              >
                Upgrade to Premium
              </button>
            </div>
          ) : shareToken ? (
            <div className="share-link-active">
              <div className="share-status">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                <span>Public link active</span>
              </div>
              <div className="share-link-input">
                <input
                  type="text"
                  value={`${window.location.origin}/shared/${shareToken}`}
                  readOnly
                />
                <button
                  className={`btn-copy ${shareCopied ? 'copied' : ''}`}
                  onClick={onCopyShareLink}
                >
                  {shareCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="share-description">
                Anyone with this link can view your list without logging in.
              </p>
              <button
                className="btn-danger-outline"
                onClick={onRevokeShareLink}
                disabled={shareLoading}
              >
                {shareLoading ? 'Revoking...' : 'Revoke Link'}
              </button>
            </div>
          ) : (
            <div className="share-link-create">
              <div className="share-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="18" cy="5" r="3"/>
                  <circle cx="6" cy="12" r="3"/>
                  <circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
              </div>
              <h3>Create Public Link</h3>
              <p>Generate a link that lets anyone view this list without logging in.</p>
              <button
                ref={generateBtnRef}
                className="btn-primary"
                onClick={onGenerateShareLink}
                disabled={shareLoading}
              >
                {shareLoading ? 'Generating...' : 'Generate Link'}
              </button>
              <ContextualTooltip
                id="share-link-first-time"
                text="Anyone with this link can view your list."
                anchorRef={generateBtnRef}
                position="top"
                show={showTooltip}
                delay={400}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ShareModal;
